import React, { useEffect, useState } from 'react';
import { Home } from 'lucide-react';
import PageHeader from '../components/PageHeader';
import { navLabel } from '../utils/navigation';
import { apiService } from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import Block from '../components/home/Block';
import HomeSearch from '../components/home/HomeSearch';
import { DoneBlock, QuarterBlock, ToFixBlock } from '../components/home/MemberBlocks';
import UpdatesBlock, { UpdatesState } from '../components/home/UpdatesBlock';
import TechHome, { TechState } from '../components/tech/TechHome';
import GiveKudosPanel from '../components/home/GiveKudosPanel';
import { HomeData } from '../components/home/types';
import { greeting, projectPath } from '../components/home/sentences';

// Home: the first screen of every role, one question each. A team member sees their quarter, what they have done,
// what to fix and what changed; a leader or an admin sees the whole of Tech (the month, the quarter's plan, the five functions,
// what changed), and an admin also the projects furthest from the standards behind Show details.
// Four blocks at most; every other page is one click away in the menu.
export default function HomePage() {
  const { user } = useAuth();
  const [home, setHome] = useState<HomeData | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [attempt, setAttempt] = useState(0);
  // The updates load on their own: a slow or failed read never holds back the rest of Home.
  const [updates, setUpdates] = useState<UpdatesState>({ status: 'loading' });
  const [updatesAttempt, setUpdatesAttempt] = useState(0);
  // Giving kudos: one panel for the Kudos block and for every project row; a row names its project.
  const [giving, setGiving] = useState<{ project: string | null } | null>(null);
  const [kudosVersion, setKudosVersion] = useState(0);
  // Tech (a leader's and an admin's Home) loads on its own too, so a slow read never holds back the greeting.
  const [tech, setTech] = useState<TechState>({ status: 'loading' });
  const [techAttempt, setTechAttempt] = useState(0);

  useEffect(() => {
    let cancelled = false;
    setError(null);
    apiService
      .getMyHome()
      .then((r) => {
        if (cancelled) return;
        if (!r.success) return setError(r.error || 'Could not load your Home');
        setHome(r.data);
      })
      .catch((e: any) => !cancelled && setError(e?.response?.data?.error || 'Could not load your Home'));
    return () => {
      cancelled = true;
    };
  }, [attempt]);

  useEffect(() => {
    let cancelled = false;
    setUpdates({ status: 'loading' });
    apiService
      .getMyUpdates()
      .then((r) => {
        if (cancelled) return;
        setUpdates(r.success ? { status: 'ready', data: r.data } : { status: 'error', message: r.error || 'Could not load the updates' });
      })
      .catch((e: any) => !cancelled && setUpdates({ status: 'error', message: e?.response?.data?.error || 'Could not load the updates' }));
    return () => {
      cancelled = true;
    };
  }, [updatesAttempt]);

  const role = user?.role === 'admin' ? 'admin' : user?.role === 'leader' ? 'leader' : 'user';
  const sharesTech = role === 'leader' || role === 'admin';

  useEffect(() => {
    if (!sharesTech) return;
    let cancelled = false;
    setTech({ status: 'loading' });
    apiService
      .getTech()
      .then((r) => {
        if (cancelled) return;
        setTech(r.success ? { status: 'ready', data: r.data } : { status: 'error', message: r.error || 'Could not read Tech' });
      })
      .catch((e: any) => !cancelled && setTech({ status: 'error', message: e?.response?.data?.error || 'Could not read Tech' }));
    return () => {
      cancelled = true;
    };
  }, [sharesTech, techAttempt]);

  const updatesBlock = (
    <UpdatesBlock state={updates} onRetry={() => setUpdatesAttempt((n) => n + 1)} role={role} className="lg:col-span-3" onGiveKudos={sharesTech ? () => setGiving({ project: null }) : undefined} />
  );
  const name = home && 'person' in home && home.person ? home.person.display_name : user?.username;

  return (
    <div className="flex-1">
      <PageHeader title={navLabel('/home')} subtitle={greeting(name, new Date().getHours())} icon={Home} />
      <div className="mx-auto max-w-5xl space-y-4 px-4 py-6 sm:px-6">
        <HomeSearch projectLink={(key) => projectPath(role, key)} personLink={role === 'leader' ? () => '/my-people' : undefined} />

        {error && (
          <div role="alert" className="rounded-2xl bg-red-50 p-4 text-sm text-red-800 ring-1 ring-inset ring-red-200">
            {error}{' '}
            <button type="button" onClick={() => setAttempt((n) => n + 1)} className="font-medium underline">
              Try again
            </button>
          </div>
        )}

        {!home && !error && <p className="text-sm text-gray-600">Loading your Home…</p>}

        {home && home.role === 'user' && home.unlinked && (
          <Block title="Not linked yet">
            <p className="text-sm text-gray-700">
              Your login is not linked to a Jira person yet, so there is nothing personal to show. Ask an admin to link it in Settings → Create users.
            </p>
          </Block>
        )}

        {home && home.role === 'user' && !home.unlinked && (
          <div className="grid grid-cols-1 gap-4 lg:grid-cols-3">
            <QuarterBlock quarter={home.quarter} />
            <DoneBlock done={home.done} hours={home.hours} accountId={home.person.account_id} />
            <ToFixBlock toFix={home.to_fix} />
            {updatesBlock}
          </div>
        )}

        {home && (home.role === 'leader' || home.role === 'admin') && (
          <TechHome
            tech={tech}
            onRetry={() => setTechAttempt((n) => n + 1)}
            updates={updatesBlock}
            attention={home.role === 'admin' ? home : null}
            onGiveKudos={(project) => setGiving({ project })}
            kudosVersion={kudosVersion}
          />
        )}
      </div>
      {(role === 'leader' || role === 'admin') && (
        <GiveKudosPanel isOpen={giving !== null} initialProject={giving?.project ?? null} onClose={() => setGiving(null)} onGiven={() => setKudosVersion((v) => v + 1)} />
      )}
    </div>
  );
}
